<template lang="html">
  <section class="month">
    <v-overlay data-cy="loading-overlay" :value="this.showMonthLoadingOverlay" absolute />

    <Flicker 
      :loading="this.loading"
      :sticky="true"
      :bottomText="monthData && monthData.user ? `Monatsabschluss: ${monthData.user.firstname} ${monthData.user.lastname}` : 'Monatsabschluss:'"
      @changeRoute="this.changeRoute">
    </Flicker>
    
    <section v-if="this.showMonth" id="showMonthData">
      <MonthTable :monthData="this.monthData" :entryUpdateEnabled="false" :attachments="this.attachments" 
        v-if="this.monthData.weeks">
      </MonthTable>

      <!-- Monatsabschluss -->
      
      <v-row class="justify-center">
        <!-- TODO: remove && this.monthData.closing.timestamp check later when we can be sure that all users use this version -->
        <v-col cols="11">
          <v-row class="justify-end">
            <v-col cols="auto">
              <v-card v-if="this.monthData.closing" class="d-flex flex-column text-center" flat color='transparent'>
                <label class="grey--text overline">Abgeschlossen am</label>
                <label id="timestamp">{{ formatTimestamp(monthData.closing.timestamp) }}</label>
              </v-card>
            </v-col>

            <v-col cols="auto">
              <v-card v-if="this.monthData.closing" class="d-flex flex-column text-center" flat color='transparent'>
                <label class="grey--text overline">
                  Status
                  <v-tooltip v-if="this.monthData.closing.latestComment" color="grey darken-4" right>
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on" color="grey lighten-1">mdi-message</v-icon>
                    </template>
                    <div id="commentbody" style="white-space: pre-line;">{{ this.monthData.closing.latestComment.body }}</div>
                  </v-tooltip>
                </label>
                <label id="status">{{ monthData.closing.status }}</label>
              </v-card>
            </v-col>
            
            <v-col cols="auto">
              <v-btn-toggle v-if="this.monthApproveableTL" dense class="ml-4">
                <v-btn id="approveMonthTL" color="#c00000" class="white--text" @click="approveMonthTL()">
                  Bestätigen
                </v-btn>
                <v-btn id="approveMonthTLWithComment" color="#c00000" class="white--text"
                  @click="approveMonthTLWithComment()">
                  <v-icon color="white">mdi-message</v-icon>
                </v-btn>
              </v-btn-toggle>

              <v-btn-toggle v-if="this.monthApproveableHR" dense class="ml-4">
                <v-btn id="approveMonthHR" color="#c00000" class="white--text" @click="approveMonthHR()">
                  Genehmigen
                </v-btn>
                <v-btn id="approveMonthHRWithComment" color="#c00000" class="white--text"
                  @click="approveMonthHRWithComment()">
                  <v-icon color="white">mdi-message</v-icon>
                </v-btn>
              </v-btn-toggle>

              <v-btn-toggle v-if="this.monthRejectable" dense class="ml-4">
                <v-btn id="rejectMonth" color="#c00000" class="white--text" @click="rejectMonth()">
                  Zurückweisen
                </v-btn>
                <v-btn id="rejectMonthWithComment" color="#c00000" class="white--text" @click="rejectMonthWithComment()">
                  <v-icon color="white">mdi-message</v-icon>
                </v-btn>
              </v-btn-toggle>

              <v-btn-toggle v-if="this.backToTLable" dense class="ml-4">
                <v-btn id="rejectMonthToTL" color="#c00000" class="white--text" @click="backToTL()">
                  Rückfrage TL
                </v-btn>
                <v-btn id="rejectMonthToTLWithComment" color="#c00000" class="white--text" @click="backToTLWithComment()">
                  <v-icon color="white">mdi-message</v-icon>
                </v-btn>
              </v-btn-toggle>

              <v-btn-toggle dense class="ml-4">
                <v-btn id="gotoMonatsansicht" color="#c00000" class="white--text" @click="gotoMonatsansicht()">
                  Zur Monatsansicht
                </v-btn>
              </v-btn-toggle>

              <v-btn-toggle v-if="this.showGotoTicket" dense class="ml-4">
                <v-btn id="gotoJiraTicket" color="#c00000" class="white--text"
                  :href="global.JIRA_SERVER_URL + '/browse/' + monthData.closing.jiraKey" target="_blank">
                  Jira Ticket
                </v-btn>
              </v-btn-toggle>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <!-- /Monatsabschluss -->

    </section>
    <v-row v-else-if="!this.loading" no-gutters class="d-flex justify-center">
      <br><br>
      Keine Daten vorhanden
    </v-row>

    <ConfirmExtended ref="confTransition"></ConfirmExtended>
    <ConfirmExtended ref="confNotFound"></ConfirmExtended>
  </section>
</template>

<script lang="js">
import dayjs from '@util/dayjs';

import Flicker from '@components/Flicker.vue';
import MonthTable from '@components/user/MonthTable.vue';
import ConfirmExtended from '@components/ConfirmExtended.vue';

import * as monatsabschlussClient from '@client/client.monatsabschluss';
import * as hrClient from '@client/client.hr';
import * as teamleadClient from '@client/client.teamlead';

import JiraMonatsabschluss from '@/mixins/JiraMonatsabschluss.vue';

export default {
  name: 'Monatsabschluss',
  components: {
    MonthTable,
    ConfirmExtended,
    Flicker
  },
  mixins: [JiraMonatsabschluss],

  created() {
    this.$root.$on('startLoading', this.startLoading);
    this.$root.$on('reload', this.loadMonth);
  },

  async mounted() {
    this.initialLoadMonth(); // absichtlich kein await, damit die "users" parallel berechnet werden

    const currentUser = this.$store.getters.staticUserData.user;
    let users = [];
    if (global.impersonationID == undefined) {
      if (currentUser.roles.isGL || currentUser.roles.isHR) {
        const hrUsers = await hrClient.getUsers();
        users = hrUsers.map((hrUsers) => {
          return {
            id: hrUsers.id,
            email: hrUsers.email
          };
        });
      } else if (currentUser.roles.isTL) {
        const teamleadUsers = await teamleadClient.getTeamData(this.$store.state.displayedMonth.year(), this.$store.state.displayedMonth.isoMonth());
        users = teamleadUsers.map((teamleadUser) => {
          return {
            id: teamleadUser.userData.mocoUserID,
            email: teamleadUser.userData.email
          };
        });
      }
    }
    this.users = users;
  },

  beforeDestroy() {
    this.$root.$off('startLoading', this.startLoading);
    this.$root.$off('reload', this.loadMonth);
  },
  
  data() {
    return {
      loading: false,
      initialMonthLoad: true,
      isFromJiraKey: true,
      monthData: {},
      attachments: [],
      users: []
    }
  },

  computed: {
    showMonth: function () {
      return (this.showMonthLoadingOverlay || (this.loading == false)) && this.monthData.user;
    },

    showMonthLoadingOverlay: function () {
      return this.loading && this.initialMonthLoad == false;
    },

    displayedDate: function () {
      return this.$store.state.displayedMonth;
    }
  },

  methods: {
    startLoading() {
      this.loading = true;
    },

    async initialLoadMonth() {
      this.initialMonthLoad = true;
      await this.loadMonth();
    },

    async loadMonth() {
      this.loading = true;
      const res = await this.$draf(async () => {
        const staticUserData = this.$store.getters.staticUserData;
        if (staticUserData == undefined || this.$store.getters.userAuthenticationData.mocoApiKey == undefined) {
          return;
        }

        const loadedJiraKey = this.$route.query?.jiraTicket;
        let jiraTicket;
        if (loadedJiraKey) {
          jiraTicket = await monatsabschlussClient.getMonatsabschluss(loadedJiraKey);
          if (jiraTicket?.month == undefined) {
            await this.openConfirmation('Monatsabschluss nicht gefunden!', `Das aufgerufene Jira-Ticket (${loadedJiraKey}) existiert nicht. Sie werden nun zum aktuellen Monat weitergeleitet.`, 'OK');
            return () => { this.$router.push({ path: global.ROUTES.aktuellermonat.path }); };
          }
          const date = dayjs(jiraTicket.month.startOfMonth);
          const username = jiraTicket.reporter.emailAddress.split('@')[0];
          return () => { this.$router.push({ name: global.ROUTES.monatsabschluss.name, params: { username: username, year: date.year(), month: date.isoMonth() } }) };
        } else {
          const userName = this.$route.params.username;
          jiraTicket = await monatsabschlussClient.getMonatsabschluss(undefined, `${userName}@seqis.com`, this.displayedDate.isoMonth(), this.displayedDate.year());
          if (jiraTicket?.month == undefined) {
            if (await this.openConfirmation('Monatsabschluss nicht gefunden!', `Für "${userName}" existiert kein Monatsabschluss in ${this.displayedDate.isoMonth()}/${this.displayedDate.year()}.`, 'Am aktuellen Monatsabschluss bleiben?  Oder zur Monatsansicht wechseln?', 'Bleiben', 'Zur Monatsansicht wechseln')) {
              return () => { this.$router.push(this.$store.state.prevRoute) }
            } else {
              return () => { this.gotoMonatsansicht(true) }
            }
          }
          this.isFromJiraKey = false;
        }

        this.loadMonthFromJiraTicket(jiraTicket);
      });

      this.initialMonthLoad = false;
      this.loading = false;
      if (typeof (res) == 'function') {
        res();
      }
    },

    async gotoMonatsansicht(gotoPrevRoute) {
      const userName = this.$route.params.username;
      const emailPrefix = `${userName}@`;
      const currentUser = this.$store.getters.staticUserData.user;
      if (currentUser.email.startsWith(emailPrefix)) {
        this.$router.push({ name: global.ROUTES.monatsansicht.name });
      } else {
        if (global.impersonationID) {
          await this.openConfirmation('Impersonierung nicht möglich!', `Der Benutzer "${userName}" kann nicht impersoniert werden, da bereits eine Impersonierung aktiv ist.`, '', 'OK');
          if (gotoPrevRoute) {
            this.$router.push(this.$store.state.prevRoute);
          }
          return;
        }

        const user = this.users.find(user => user.email.startsWith(emailPrefix));
        if (user == undefined) {
          await this.openConfirmation('Benutzer nicht gefunden!', `Der Benutzer "${userName}" wurde nicht gefunden und kann nicht impersoniert werden.`, '', 'OK');
          if (gotoPrevRoute) {
            this.$router.push(this.$store.state.prevRoute);
          }
          return;
        }

        if (await this.openConfirmation('Benutzer impersonieren?', `Soll der Benutzer "${userName}" impersoniert und zu dessen Monatsansicht gewechselt werden?`, '', 'Bleiben', 'Impersonieren')) {
          if (gotoPrevRoute) {
            this.$router.push(this.$store.state.prevRoute);
          }
        } else {
          const monatsansichtUrl = {
            name: global.ROUTES.monatsansicht.name,
            params: {
              username: this.$route.params.username,
              year: this.$store.state.displayedMonth.year(),
              month: this.$store.state.displayedMonth.isoMonth()
            },
            query: {
              impersonation: user.id
            }
          }
          window.open(this.$router.resolve(monatsansichtUrl).href, '_blank');
        }
      }
    },

    async openConfirmation(title, message, details, confirmText, cancelText) {
      this.loading = false;
      return (await this.$refs.confNotFound.open({ title, message, details },{ confirmText, cancelText })).confirmed;
    },

    changeRoute(displayedDate) {
      this.$router.push({
        name: global.ROUTES.monatsabschluss.name,
        params: {
          username: this.$route.params.username,
          year: displayedDate.year(),
          month: displayedDate.isoMonth()
        }
      });
    }
  },
  watch: {
    displayedDate: function () {
      this.initialLoadMonth();
    }
  },
}
</script>
<style type="scss">
#commentbody {
  white-space: pre-line;
}

.v-btn-toggle {
  box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12);
}

.v-btn-toggle>.v-btn {
  opacity: 1 !important;
}
</style>